import {
  Box, Button, CircularProgress, Drawer, MenuItem, TextField, Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import React from 'react';
import { ProductOnUser } from 'common-ts/dist/models/Product';
import { terbebasClient } from '../terbebasApi';
import { formatRupiah, RegionPrefill } from './LinkPageShared';

// "Cek Ongkir" dialog on the /link page: pick Provinsi → Kota → Kecamatan
// (same KiriminAja region endpoints the checkout address form uses), quote
// rates, and hand the chosen region to buy-now so checkout arrives with the
// address form prefilled.

// Mirrors the checkout's courier pricing so the estimate matches what the
// shopper later sees (PageCheckoutCourier costMarkup). Store subsidies are
// computed at checkout, so the real price can only get cheaper.
const COST_MARKUP = 7000;
const DEFAULT_WEIGHT_GRAMS = 1000;

const useStyles = makeStyles((theme: Theme) =>
createStyles({
  sheet: {
    borderRadius: '16px 16px 0px 0px',
    padding: '22px 20px 26px',
    maxWidth: '480px',
    margin: '0 auto',
  },
  title: {
    fontSize: '17px',
    fontWeight: 700,
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#8a8a8a',
    marginBottom: '12px',
  },
  select: {
    marginBottom: '12px',
    width: '100%',
  },
  rateRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: '9px 12px',
    border: '1px solid #ececec',
    borderRadius: '10px',
    marginBottom: '8px',
  },
  rateName: {
    fontSize: '13px',
    fontWeight: 600,
  },
  rateEtd: {
    fontSize: '11px',
    color: '#8a8a8a',
  },
  ratePrice: {
    fontSize: '14px',
    fontWeight: 700,
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    padding: '18px 0px',
  },
  note: {
    fontSize: '11px',
    color: '#8a8a8a',
    margin: '4px 0px 12px',
  },
  ctaButton: {
    width: '100%',
    padding: '12px 0px',
    borderRadius: '10px',
    fontWeight: 700,
  },
}),
);

interface RegionOption {
  id: number;
  [key: string]: unknown;
}

interface RateOption {
  service: string;
  service_name: string;
  cost: number | string;
  etd: string;
  group?: string;
}

interface LinkPageOngkirDialogProps {
  open: boolean;
  onClose: () => void;
  // Buy-now context: the product whose weight drives the quote. null = no
  // product context (generic 1 kg estimate, CTA hidden).
  product: ProductOnUser | null;
  // "Lanjut Beli" with the picked region; parent runs buy-now + prefill.
  onLanjutBeli: (product: ProductOnUser, prefill: RegionPrefill) => void;
}

export default function LinkPageOngkirDialog({ open, onClose, product, onLanjutBeli }: LinkPageOngkirDialogProps) {
  const classes = useStyles();

  const [provinces, setProvinces] = React.useState<RegionOption[] | null>(null);
  const [cities, setCities] = React.useState<RegionOption[] | null>(null);
  const [districts, setDistricts] = React.useState<RegionOption[] | null>(null);
  const [selectedProvince, setSelectedProvince] = React.useState<RegionOption | null>(null);
  const [selectedCity, setSelectedCity] = React.useState<RegionOption | null>(null);
  const [selectedDistrict, setSelectedDistrict] = React.useState<RegionOption | null>(null);
  const [rates, setRates] = React.useState<RateOption[] | null>(null);
  const [loadingRates, setLoadingRates] = React.useState(false);

  React.useEffect(() => {
    if (!open || provinces) return;
    terbebasClient.get('/storefront/shipping/regions/provinces')
      .then((res) => setProvinces(res.data?.datas ?? []))
      .catch(() => setProvinces([]));
  }, [open, provinces]);

  // The estimate quotes one piece of the first variant. The server derives the
  // parcel (weight + dimensions) from that variant, exactly as the real
  // checkout would — see computeShipmentPackage in the backend.
  const quoteItems = (() => {
    const first = (product?.commerce?.variants ?? [])[0];
    return first ? [{ variantId: first.id, quantity: 1 }] : [];
  })();

  const pickProvince = (option: RegionOption) => {
    setSelectedProvince(option);
    setSelectedCity(null);
    setSelectedDistrict(null);
    setCities(null);
    setDistricts(null);
    setRates(null);
    terbebasClient.get('/storefront/shipping/regions/cities', { params: { parentId: option.id } })
      .then((res) => setCities(res.data?.datas ?? []))
      .catch(() => setCities([]));
  };

  const pickCity = (option: RegionOption) => {
    setSelectedCity(option);
    setSelectedDistrict(null);
    setDistricts(null);
    setRates(null);
    terbebasClient.get('/storefront/shipping/regions/districts', { params: { parentId: option.id } })
      .then((res) => setDistricts(res.data?.datas ?? []))
      .catch(() => setDistricts([]));
  };

  const pickDistrict = (option: RegionOption) => {
    setSelectedDistrict(option);
    setRates(null);
    setLoadingRates(true);
    terbebasClient.post('/storefront/shipping/rates', {
      destinationDistrictId: option.id,
      items: quoteItems,
    })
      .then((res) => {
        const results: RateOption[] = res.data?.results ?? [];
        // Same filter the checkout applies: drop same-day / broken entries.
        const usable = results
          .filter((item) => item.etd && item.etd !== '-'
            && !item.service_name?.toLowerCase()?.includes('same')
            && !item.group?.includes('one_day'))
          .sort((a, b) => Number(a.cost) - Number(b.cost))
          .slice(0, 3);
        setRates(usable);
      })
      .catch(() => setRates([]))
      .finally(() => setLoadingRates(false));
  };

  const provinceName = (o: RegionOption) => String(o.provinsi_name ?? '');
  const cityName = (o: RegionOption) => `${o.type ?? ''} ${o.kabupaten_name ?? ''}`.trim();
  const districtName = (o: RegionOption) => String(o.kecamatan_name ?? '');

  const buildPrefill = (): RegionPrefill | null => {
    if (!selectedProvince || !selectedCity || !selectedDistrict) return null;
    const kodePos = Number(selectedDistrict.postal_code) || undefined;
    return {
      provinsi: provinceName(selectedProvince),
      kabupaten: cityName(selectedCity),
      kecamatan: districtName(selectedDistrict),
      kodePos,
      // Exact shape OverlayViewAddressEdit stores as destinationKiriminAjaObject
      // (feeds shippingAddress.kiriminAjaDestination at order submit).
      destinationKiriminAjaObject: {
        provinsi: provinceName(selectedProvince),
        provinsi_id: selectedProvince.id,
        kabupaten: cityName(selectedCity),
        kabupaten_id: selectedCity.id,
        kecamatan: districtName(selectedDistrict),
        kecamatan_id: selectedDistrict.id,
        kode_pos: selectedDistrict.postal_code,
      },
    };
  };

  const renderSelect = (
    label: string,
    options: RegionOption[] | null,
    selected: RegionOption | null,
    getName: (o: RegionOption) => string,
    onPick: (o: RegionOption) => void,
    disabled: boolean,
  ) => (
    <TextField
      select
      className={classes.select}
      label={label}
      variant="outlined"
      size="small"
      disabled={disabled}
      value={selected ? String(selected.id) : ''}
      onChange={(event) => {
        const option = (options ?? []).find((o) => String(o.id) === event.target.value);
        if (option) onPick(option);
      }}
    >
      {
        (options ?? []).map((option) => (
          <MenuItem key={option.id} value={String(option.id)}>
            {getName(option)}
          </MenuItem>
        ))
      }
    </TextField>
  );

  const prefillReady = buildPrefill();

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{ className: classes.sheet }}
    >
      <Typography className={classes.title}>Cek Ongkir</Typography>
      <Typography className={classes.subtitle}>
        {product
          ? `Estimasi ongkir untuk ${product.name}`
          : 'Estimasi ongkir (berat 1 kg)'}
      </Typography>
      {renderSelect('Provinsi', provinces, selectedProvince, provinceName, pickProvince, !provinces)}
      {renderSelect('Kota / Kabupaten', cities, selectedCity, cityName, pickCity, !cities)}
      {renderSelect('Kecamatan', districts, selectedDistrict, districtName, pickDistrict, !districts)}
      {
        loadingRates &&
        <Box className={classes.loadingBox}>
          <CircularProgress size={26} />
        </Box>
      }
      {
        rates !== null && !loadingRates && (
          rates.length ?
          <>
            {
              rates.map((rate) => (
                <Box key={rate.service + rate.service_name} className={classes.rateRow}>
                  <Box>
                    <Typography className={classes.rateName}>{rate.service_name}</Typography>
                    <Typography className={classes.rateEtd}>Estimasi {rate.etd} hari</Typography>
                  </Box>
                  <Typography className={classes.ratePrice}>
                    {formatRupiah(Number(rate.cost) + COST_MARKUP)}
                  </Typography>
                </Box>
              ))
            }
            <Typography className={classes.note}>
              Estimasi. Subsidi ongkir toko dihitung saat checkout — bisa lebih murah.
            </Typography>
          </> :
          <Typography className={classes.note}>
            Ongkir untuk daerah ini belum tersedia. Coba kecamatan lain ya.
          </Typography>
        )
      }
      {
        product && (
        <Button
          className={classes.ctaButton}
          variant="contained"
          color="primary"
          disableElevation
          disabled={!prefillReady}
          onClick={() => {
            const prefill = buildPrefill();
            if (product && prefill) onLanjutBeli(product, prefill);
          }}
        >
          Lanjut Beli
        </Button>
        )
      }
    </Drawer>
  );
}
